import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiPlus,
  FiDownload,
  FiUpload,
  FiChevronDown,
  FiCheckSquare,
  FiSquare,
  FiTrash2,
  FiArrowRight,
  FiX,
  FiBarChart2,
} from "react-icons/fi";

import Loader from "../../Global/Loader";
import KanbanColumn from "./KanbanColumn";
import LeadDetailModal from "./LeadDetailModal";
import FilterBar from "./FilterBar";
import StageFormModal from "./StageFormModal";
import LeadFormModal from "./LeadFormModal";
import ImportLeadsModal from "./ImportLeadsModal";
import WinCelebration, { isWonStage } from "./WinCelebration";

import { UserContext } from "../../../context/UserContext";
import {
  fetchKanbanBoardDetail,
  fetchLeads,
  moveLeadToStage,
  updateStage,
  deleteStage,
  exportBoardExcel,
  exportBoardPDF,
  handleExportDownload,
  fetchAllConsultants,
  bulkMoveLeads,
  bulkDeleteLeads,
} from "../../../services/crmApi";
import { fetchSalesPersonsNormalized } from "../../../services/salesPersonApi";

const defaultFilters = {
  tags: [],
  category: "",
  priority: "",
  status: "",
  leadSource: "",
  assignedTo: "",
  salesPerson: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const KanbanBoardView = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { userRole } = useContext(UserContext);
  const isAdmin = userRole?.toLowerCase() === "admin";
  const isConsultant = userRole?.toLowerCase() === "consultant";
  const basePath = isAdmin ? "/admin/crm" : isConsultant ? "/consultant/crm" : "/sales/crm";

  const [board, setBoard] = useState(null);
  const [stages, setStages] = useState([]); // [{...column, leads: []}]
  const [loading, setLoading] = useState(true);
  const [refreshingLeads, setRefreshingLeads] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modals & UI state
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [defaultStageForNewLead, setDefaultStageForNewLead] = useState(null);
  const [confirmDeleteStage, setConfirmDeleteStage] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Filters / sort / search
  const [filters, setFilters] = useState(defaultFilters);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Drag tracking
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [draggingColumnId, setDraggingColumnId] = useState(null);

  // Bulk selection state — used by both Admin & SalesPerson roles to move
  // multiple leads to a stage at once or delete them in one shot.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => new Set());
  const [bulkMoveStageId, setBulkMoveStageId] = useState("");
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [animationLeadCount, setAnimationLeadCount] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);

  // Pickers (for filter & lead form dropdowns)
  const [consultants, setConsultants] = useState([]);
  const [combinedSalesPersons, setCombinedSalesPersons] = useState([]);

  // "Won stage" celebration. We store an opaque `sessionId` so each new
  // win starts a fresh animation (via React `key`), while during a
  // celebration any unrelated re-render of this view leaves the
  // celebration's DOM nodes (and therefore its CSS animations) intact.
  const [celebration, setCelebration] = useState(null);

  const triggerWinCelebration = useCallback((leadName, stageName) => {
    setCelebration({
      sessionId:
        (typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now()) + Math.random(),
      leadName,
      stageName,
    });
  }, []);

  // Stable reference — `<WinCelebration />` reads it via a ref, but
  // keeping it stable also satisfies React.memo and any future deps.
  const handleCelebrationComplete = useCallback(() => {
    setCelebration(null);
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadBoardData = useCallback(async () => {
    if (!boardId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetchKanbanBoardDetail(boardId);
      const boardData = response?.data;
      if (!boardData) {
        setError("Board not found");
        return;
      }

      const sortedColumns = (boardData.columns || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((col) => ({
          ...col,
          leads: Array.isArray(col.leads) ? col.leads : [],
        }));

      setBoard(boardData);
      setStages(sortedColumns);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to load board",
      );
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  // Re-fetch leads from server (with current filters/search/sort) and group by stage
  const reloadLeadsWithFilters = useCallback(async () => {
    if (!boardId || stages.length === 0) return;
    try {
      setRefreshingLeads(true);
      const response = await fetchLeads(boardId, {
        search: debouncedSearch || undefined,
        category: filters.category || undefined,
        tags: filters.tags,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        leadSource: filters.leadSource || undefined,
        assignedTo: filters.assignedTo || undefined,
        salesPerson: isAdmin ? filters.salesPerson || undefined : undefined,
        sortBy: filters.sortBy || "createdAt",
        sortOrder: filters.sortOrder || "desc",
        limit: 500,
      });

      const leads = response?.data?.leads || response?.data || [];
      // Group leads by column id
      const byColumn = new Map();
      leads.forEach((l) => {
        const colId =
          (l.column && (l.column._id || l.column)) || l.columnId || null;
        if (!colId) return;
        if (!byColumn.has(colId)) byColumn.set(colId, []);
        byColumn.get(colId).push(l);
      });

      setStages((prev) =>
        prev.map((s) => ({ ...s, leads: byColumn.get(s._id) || [] })),
      );
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setRefreshingLeads(false);
    }
  }, [boardId, debouncedSearch, filters, isAdmin, stages.length]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  // Load pickers for filters/forms (admin only — sales persons can't read these)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const consRes = await fetchAllConsultants();
        const consList = Array.isArray(consRes?.data) ? consRes.data : [];
        setConsultants(consList.map((c) => ({ ...c, _id: c._id || c.id })));
      } catch (_) {}

      try {
        const normalized = await fetchSalesPersonsNormalized();
        setCombinedSalesPersons(normalized);
      } catch (_) {
        setCombinedSalesPersons([]);
      }
    })();
  }, [isAdmin]);

  // Mark the board as "ready for filter-driven reloads" once the initial board
  // data has been loaded. This is intentionally a SEPARATE effect from the
  // filter-watch effect below — keeping them apart means the very first user
  // filter/sort/search interaction actually triggers an API call (the previous
  // version flipped the flag in the same effect, so the first user change was
  // silently eaten).
  const hasLoadedBoardRef = useRef(false);
  useEffect(() => {
    if (!loading && stages.length > 0) {
      hasLoadedBoardRef.current = true;
    }
  }, [loading, stages.length]);

  // When filters/search/sort change, refetch leads from the backend.
  useEffect(() => {
    if (!hasLoadedBoardRef.current) return;
    reloadLeadsWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    filters.category,
    filters.priority,
    filters.status,
    filters.leadSource,
    filters.assignedTo,
    filters.salesPerson,
    filters.sortBy,
    filters.sortOrder,
    JSON.stringify(filters.tags),
  ]);

  // Derive available tags & categories from leads
  const { availableTags, availableCategories } = useMemo(() => {
    const tagSet = new Set();
    const catSet = new Set();
    stages.forEach((s) => {
      s.leads.forEach((l) => {
        (l.tags || []).forEach((t) => t && tagSet.add(t));
        if (l.category) catSet.add(l.category);
      });
    });
    return {
      availableTags: Array.from(tagSet).sort(),
      availableCategories: Array.from(catSet).sort(),
    };
  }, [stages]);

  // ===== Lead drag & drop handlers =====
  const handleLeadDragStart = (lead) => setDraggingLeadId(lead._id);
  const handleLeadDragEnd = () => setDraggingLeadId(null);

  const handleDropLead = async (leadId, targetStageId, position) => {
    setDraggingLeadId(null);

    let movedLead = null;
    let sourceStageId = null;
    let targetStageName = null;
    stages.forEach((s) => {
      if (s._id === targetStageId) {
        targetStageName = s.columnName;
      }
      s.leads.forEach((l) => {
        if (l._id === leadId) {
          movedLead = l;
          sourceStageId = s._id;
        }
      });
    });
    if (!movedLead) return;
    if (sourceStageId === targetStageId) return;

    const targetStage = stages.find((s) => s._id === targetStageId);
    const previous = stages;

    // Optimistic update
    setStages((prev) =>
      prev.map((s) => {
        if (s._id === sourceStageId) {
          return { ...s, leads: s.leads.filter((l) => l._id !== leadId) };
        }
        if (s._id === targetStageId) {
          return {
            ...s,
            leads: [...s.leads, { ...movedLead, column: targetStageId }],
          };
        }
        return s;
      }),
    );

    try {
      await moveLeadToStage(boardId, leadId, targetStageId, position);
      if (isWonStage(targetStage)) {
        triggerWinCelebration(movedLead?.name, targetStage?.columnName);
      }
    } catch (err) {
      setStages(previous);
    }
  };

  // ===== Column drag & drop handlers =====
  const handleColumnDragStart = (stageId) => setDraggingColumnId(stageId);
  const handleColumnDragEnd = () => setDraggingColumnId(null);

  const reorderColumns = async (fromStageId, toStageId, side) => {
    setDraggingColumnId(null);
    if (fromStageId === toStageId) return;

    const fromIdx = stages.findIndex((s) => s._id === fromStageId);
    const toIdx = stages.findIndex((s) => s._id === toStageId);
    if (fromIdx === -1 || toIdx === -1) return;

    const newStages = [...stages];
    const [moved] = newStages.splice(fromIdx, 1);
    let insertAt = newStages.findIndex((s) => s._id === toStageId);
    if (side === "after") insertAt += 1;
    newStages.splice(insertAt, 0, moved);

    const previous = stages;
    setStages(newStages);

    try {
      setActionLoading(true);
      await updateStage(boardId, fromStageId, {
        order: insertAt + 1,
      });
    } catch (err) {
      setStages(previous);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDropColumnBefore = (fromStageId, toStageId) =>
    reorderColumns(fromStageId, toStageId, "before");
  const handleDropColumnAfter = (fromStageId, toStageId) =>
    reorderColumns(fromStageId, toStageId, "after");

  // ===== Stage CRUD =====
  const handleAddStage = () => {
    setEditingStage(null);
    setShowStageModal(true);
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setShowStageModal(true);
  };

  const handleStageSaved = async () => {
    setShowStageModal(false);
    setEditingStage(null);
    await loadBoardData();
  };

  const handleConfirmDeleteStage = async () => {
    if (!confirmDeleteStage) return;
    try {
      setActionLoading(true);
      await deleteStage(boardId, confirmDeleteStage._id);
      toast.success("Stage deleted");
      setConfirmDeleteStage(null);
      await loadBoardData();
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setActionLoading(false);
    }
  };

  // ===== Lead CRUD =====
  const handleAddLead = (stage = null) => {
    setEditingLead(null);
    setDefaultStageForNewLead(stage?._id || stages[0]?._id || null);
    setShowLeadModal(true);
  };

  const handleLeadSaved = async () => {
    setShowLeadModal(false);
    setEditingLead(null);
    setDefaultStageForNewLead(null);
    await loadBoardData();
  };

  const handleOpenLeadDetails = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  // ===== Search / Filter / Sort =====
  const handleSearch = (q) => setSearchTerm(q);
  const handleFilter = (f) => setFilters(f);
  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setSearchTerm("");
  };

  // ===== Bulk select / move / delete =====
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedLeadIds(new Set());
    setBulkMoveStageId("");
    setConfirmBulkDelete(false);
  }, []);

  const toggleSelectMode = () => {
    if (selectMode) {
      exitSelectMode();
    } else {
      setSelectMode(true);
    }
  };

  const handleToggleSelectLead = useCallback((leadId) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }, []);

  const handleSelectAllVisible = () => {
    const allIds = stages.flatMap((s) => s.leads.map((l) => l._id));
    setSelectedLeadIds(new Set(allIds));
  };

  const handleClearSelection = () => setSelectedLeadIds(new Set());

  const handleBulkMove = async () => {
    if (!bulkMoveStageId) {
      toast.error("Please choose a stage to move the selected leads to");
      return;
    }
    const ids = Array.from(selectedLeadIds);
    if (ids.length === 0) return;
    const targetStage = stages.find((s) => s._id === bulkMoveStageId);
    // Capture a representative lead name BEFORE we exit select mode /
    // reload the board (which would clear `selectedLeadIds`).
    let firstSelectedLeadName = null;
    if (isWonStage(targetStage)) {
      for (const s of stages) {
        const found = s.leads.find((l) => ids.includes(l._id));
        if (found) {
          firstSelectedLeadName =
            ids.length === 1
              ? found.name
              : `${ids.length} leads`;
          break;
        }
      }
    }
    try {
      setBulkActionLoading(true);
      await bulkMoveLeads(boardId, ids, bulkMoveStageId);
      toast.success(
        `Moved ${ids.length} ${ids.length === 1 ? "lead" : "leads"}`,
      );
      if (isWonStage(targetStage)) {
        triggerWinCelebration(firstSelectedLeadName, targetStage?.columnName);
      }
      exitSelectMode();
      await loadBoardData();

      // Trigger success animation if moved to Won stage
      if (isWonStage(targetStage)) {
        setAnimationLeadCount(ids.length);
        setAnimationKey((prev) => prev + 1);
        setShowSuccessAnimation(true);
      }
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedLeadIds);
    if (ids.length === 0) return;
    try {
      setBulkActionLoading(true);
      await bulkDeleteLeads(boardId, ids);
      toast.success(
        `Deleted ${ids.length} ${ids.length === 1 ? "lead" : "leads"}`,
      );
      exitSelectMode();
      await loadBoardData();
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setBulkActionLoading(false);
    }
  };

  // ===== Export / Import =====
  const handleExport = async (format) => {
    try {
      setActionLoading(true);
      setShowExportMenu(false);
      let response;
      switch (format) {
        case "excel":
          response = await exportBoardExcel(boardId);
          handleExportDownload(response, "xlsx");
          break;
        case "pdf":
          response = await exportBoardPDF(boardId);
          handleExportDownload(response, "pdf");
          break;
        default:
          break;
      }
      toast.success(`Board exported as ${format.toUpperCase()}`);
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Loader />;

  const totalLeads = stages.reduce((sum, s) => sum + s.leads.length, 0);

  return (
    <div className="min-h-screen bg-[#f4f8fb] flex flex-col pt-16 sm:mt-16 pl-3 pr-3 pb-4 sm:pl-36 sm:pr-6">
      {actionLoading && <Loader />}

      {/* Header — stacks on narrow viewports; full width with sidebar offset on sm+ */}
      <div className="bg-white shadow-md p-4 sm:p-6 flex-shrink-0 border-b border-gray-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => navigate(basePath)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-0.5 flex-shrink-0 touch-manipulation"
              title="Back to boards"
            >
              <FiArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 break-words">
                {board?.name}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5 break-words">
                {board?.description || "No description"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {stages.length} {stages.length === 1 ? "stage" : "stages"} •{" "}
                {totalLeads} {totalLeads === 1 ? "lead" : "leads"}
                {refreshingLeads && (
                  <span className="ml-2 text-blue-500">Refreshing…</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:justify-end sm:flex-shrink-0">
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/${boardId}/dashboard`)}
                className="px-3 py-2 bg-teal-700 hover:bg-teal-800 text-white text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors shadow-sm flex-1 min-w-[8.5rem] sm:flex-initial touch-manipulation"
                title="Open CRM dashboard for this board"
              >
                <FiBarChart2 size={16} />
                Dashboard
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={handleAddStage}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors shadow-sm flex-1 min-w-[8.5rem] sm:flex-initial touch-manipulation"
              >
                <FiPlus size={16} />
                Add Stage
              </button>
            )}

            <button
              type="button"
              onClick={() => handleAddLead(null)}
              disabled={stages.length === 0}
              className="px-3 py-2 bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 min-w-[8.5rem] sm:flex-initial touch-manipulation"
              title={
                stages.length === 0
                  ? "Add at least one stage first"
                  : "Add a new lead"
              }
            >
              <FiPlus size={16} />
              Add Lead
            </button>

            <button
              type="button"
              onClick={toggleSelectMode}
              disabled={stages.length === 0}
              className={`px-3 py-2 text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 min-w-[8.5rem] sm:flex-initial touch-manipulation ${
                selectMode
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
              title={
                selectMode
                  ? "Exit selection mode"
                  : "Select leads in bulk to move or delete"
              }
            >
              {selectMode ? (
                <FiCheckSquare size={16} />
              ) : (
                <FiSquare size={16} />
              )}
              {selectMode ? "Selecting" : "Select"}
            </button>

            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              disabled={actionLoading || stages.length === 0}
              className={`px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors flex-1 min-w-[8.5rem] sm:flex-initial touch-manipulation ${
                stages.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              title={
                stages.length === 0
                  ? "Add at least one stage first"
                  : "Import leads from the exported Excel template (activities and notes preserved if present)"
              }
            >
              <FiUpload size={16} />
              Import
            </button>

            <div className="relative flex-1 min-w-[8.5rem] sm:flex-initial sm:min-w-0">
              <button
                type="button"
                onClick={() => setShowExportMenu((v) => !v)}
                onBlur={() => setTimeout(() => setShowExportMenu(false), 150)}
                className="w-full sm:w-auto px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md flex items-center justify-center gap-1.5 transition-colors touch-manipulation"
              >
                <FiDownload size={16} />
                Export
                <FiChevronDown size={14} />
              </button>
              {showExportMenu && (
                <div className="absolute left-0 right-0 sm:left-auto sm:right-0 top-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 z-30 min-w-[140px]">
                  <button
                    type="button"
                    onMouseDown={() => handleExport("excel")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  >
                    Export as Excel
                  </button>
                  <button
                    type="button"
                    onMouseDown={() => handleExport("pdf")}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm border-t border-gray-100"
                  >
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <FilterBar
          searchValue={searchTerm}
          onSearch={handleSearch}
          filters={filters}
          onFilter={handleFilter}
          onClearFilters={handleClearFilters}
          availableTags={availableTags}
          availableCategories={availableCategories}
          availableConsultants={consultants}
          availableSalesPersons={combinedSalesPersons}
          isAdmin={isAdmin}
        />

        {/* Bulk action bar (visible while selection mode is on) */}
        {selectMode && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[160px]">
              <FiCheckSquare className="text-blue-600" size={18} />
              <span className="text-sm font-medium text-blue-900">
                {selectedLeadIds.size}{" "}
                {selectedLeadIds.size === 1 ? "lead" : "leads"} selected
              </span>
              {totalLeads > 0 && selectedLeadIds.size < totalLeads && (
                <button
                  type="button"
                  onClick={handleSelectAllVisible}
                  className="text-xs text-blue-700 underline hover:text-blue-900"
                >
                  Select all visible ({totalLeads})
                </button>
              )}
              {selectedLeadIds.size > 0 && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="text-xs text-gray-600 underline hover:text-gray-800"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <select
                value={bulkMoveStageId}
                onChange={(e) => setBulkMoveStageId(e.target.value)}
                disabled={selectedLeadIds.size === 0 || bulkActionLoading}
                className="w-full sm:w-auto min-w-0 flex-1 sm:flex-initial px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:opacity-50"
              >
                <option value="">Move to stage...</option>
                {stages.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.columnName}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleBulkMove}
                disabled={
                  selectedLeadIds.size === 0 ||
                  !bulkMoveStageId ||
                  bulkActionLoading
                }
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiArrowRight size={14} />
                Move
              </button>

              <button
                type="button"
                onClick={() => setConfirmBulkDelete(true)}
                disabled={selectedLeadIds.size === 0 || bulkActionLoading}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiTrash2 size={14} />
                Delete
              </button>

              <button
                type="button"
                onClick={exitSelectMode}
                disabled={bulkActionLoading}
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm rounded-md flex items-center gap-1.5 disabled:opacity-50"
              >
                <FiX size={14} />
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 flex justify-between items-center flex-shrink-0">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-4 underline hover:no-underline text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Kanban board: vertical scroll for page, horizontal scroll for columns on small screens */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-3 sm:p-6">
        {stages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-700 text-lg font-medium mb-2">
              No stages yet
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {isAdmin
                ? "Stages represent the columns of your pipeline. Create your first one to begin organizing leads."
                : "Ask your admin to set up stages for this board."}
            </p>
            {isAdmin && (
              <button
                type="button"
                onClick={handleAddStage}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md inline-flex items-center gap-2"
              >
                <FiPlus size={18} />
                Create Your First Stage
              </button>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto overscroll-x-contain touch-pan-x -mx-1 px-1 sm:mx-0 sm:px-0 pb-2">
            <div className="flex gap-3 sm:gap-4 w-max min-h-full items-start pr-2">
              {stages.map((stage) => (
                <KanbanColumn
                  key={stage._id}
                  stage={stage}
                  leads={stage.leads}
                  onDropLead={handleDropLead}
                  onDropColumnBefore={handleDropColumnBefore}
                  onDropColumnAfter={handleDropColumnAfter}
                  onOpenLeadDetails={handleOpenLeadDetails}
                  onEditStage={handleEditStage}
                  onDeleteStage={(s) => setConfirmDeleteStage(s)}
                  onAddLead={handleAddLead}
                  onLeadDragStart={handleLeadDragStart}
                  onLeadDragEnd={handleLeadDragEnd}
                  onColumnDragStart={handleColumnDragStart}
                  onColumnDragEnd={handleColumnDragEnd}
                  draggingLeadId={draggingLeadId}
                  draggingColumnId={draggingColumnId}
                  isAdmin={isAdmin}
                  selectMode={selectMode}
                  selectedLeadIds={selectedLeadIds}
                  onToggleSelectLead={handleToggleSelectLead}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLeadDetail && selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          boardId={boardId}
          stages={stages}
          consultants={consultants}
          salesPersons={combinedSalesPersons}
          isAdmin={isAdmin}
          onClose={() => {
            setShowLeadDetail(false);
            setSelectedLead(null);
          }}
          onUpdate={loadBoardData}
        />
      )}

      {showStageModal && (
        <StageFormModal
          boardId={boardId}
          stage={editingStage}
          onClose={() => {
            setShowStageModal(false);
            setEditingStage(null);
          }}
          onSave={handleStageSaved}
        />
      )}

      {showLeadModal && (
        <LeadFormModal
          boardId={boardId}
          stages={stages}
          defaultStageId={defaultStageForNewLead}
          lead={editingLead}
          consultants={consultants}
          salesPersons={combinedSalesPersons}
          isAdmin={isAdmin}
          onClose={() => {
            setShowLeadModal(false);
            setEditingLead(null);
            setDefaultStageForNewLead(null);
          }}
          onSave={handleLeadSaved}
        />
      )}

      {showImportModal && (
        <ImportLeadsModal
          boardId={boardId}
          onClose={() => setShowImportModal(false)}
          onImported={loadBoardData}
        />
      )}

      {confirmBulkDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Delete Selected Leads
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {selectedLeadIds.size}{" "}
                {selectedLeadIds.size === 1 ? "lead" : "leads"}
              </span>
              ? This will permanently remove them along with their notes. This
              action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmBulkDelete(false)}
                disabled={bulkActionLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkActionLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {bulkActionLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Won" celebration overlay. The `key` ties the mounted instance
          to a single celebration session — re-renders of this view do
          NOT change the key, so the underlying DOM nodes stay alive
          and the CSS animation keeps progressing. A new win produces a
          new sessionId → a fresh remount → a fresh animation. */}
      {celebration && (
        <WinCelebration
          key={celebration.sessionId}
          leadName={celebration.leadName}
          stageName={celebration.stageName}
          onComplete={handleCelebrationComplete}
        />
      )}

      {confirmDeleteStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Delete Stage
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{confirmDeleteStage.columnName}"
              </span>
              ? This will permanently remove the stage and all its leads.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setConfirmDeleteStage(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteStage}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoardView;
